import React, { useState, useRef } from 'react';
import { X, Gift } from 'lucide-react';
import Button from '../ui/Button';
import ImageUploader from '../ui/ImageUploader';
import { useToast } from '../../hooks/useToast';

/**
 * 作废记录对话框
 * @param {Object} props
 * @param {boolean} props.isOpen - 是否显示对话框
 * @param {Object} props.record - 要作废的记录数据
 * @param {number} props.remainingHours - 剩余可作废课时
 * @param {number} props.remainingAmount - 剩余可作废金额
 * @param {Function} props.onConfirm - 确认作废回调
 * @param {Function} props.onCancel - 取消回调
 */
const VoidRecordDialog = ({
  isOpen,
  record,
  remainingHours = 0,
  remainingAmount = 0,
  onConfirm,
  onCancel
}) => {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [proofUrls, setProofUrls] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 防重入锁（同步），见 docs/ASYNC_LOCK_PATTERN.md
  const isSubmittingRef = useRef(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!reason.trim()) {
      toast.error('请输入作废原因');
      return;
    }

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onConfirm({
        reason: reason.trim(),
        proof_urls: proofUrls
      });
      // 清空表单
      setReason('');
      setProofUrls([]);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setReason('');
    setProofUrls([]);
    onCancel();
  };

  // 计算可作废的课时和金额
  const voidableHours = Math.min(remainingHours, record?.total_hours || 0);
  const voidableAmount = Math.min(remainingAmount, record?.amount_paid || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-[calc(100%-2rem)] max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">作废财务记录</h2>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isSubmitting}
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* 记录信息 */}
          <div className="bg-blue-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">学生姓名</span>
              <span className="font-medium text-gray-800">{record?.student_name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">课程名称</span>
              <span className="font-medium text-gray-800">
                {record?.course_name?.split('｜')[0] || record?.course_name}
              </span>
            </div>
            <div className="h-px bg-blue-200 my-2" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">购买总课时</span>
              <span className="text-lg font-bold text-gray-800">{record?.total_hours || 0} 课时</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">购买总金额</span>
              <span className="text-lg font-bold text-gray-800">¥{record?.amount_paid || 0}</span>
            </div>
          </div>

          {/* 可作废信息 */}
          <div className="bg-orange-50 rounded-xl p-4 space-y-2">
            <div className="text-sm font-medium text-orange-700 mb-2">可作废剩余额度</div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">剩余课时</span>
              <span className="text-lg font-bold text-orange-600">{remainingHours} 课时</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">剩余金额</span>
              <span className="text-lg font-bold text-orange-600">¥{remainingAmount}</span>
            </div>
            {(voidableHours < (record?.total_hours || 0)) && (
              <div className="text-xs text-orange-600 mt-2">
                ⚠️ 注意：该课程已部分消费，仅能作废剩余部分
              </div>
            )}
          </div>
          
          {/* 赠送处理说明 */}
          {(record?.referral_info || record?.volume_bonus_info || (record?.reward_ledgers && record.reward_ledgers.length > 0)) && (
            <div className="bg-purple-50 rounded-xl p-4 space-y-2 border border-purple-100">
              <div className="text-sm font-bold text-purple-700 flex items-center gap-2">
                <Gift size={16} />
                关联赠送处理说明
              </div>
              <div className="text-xs text-purple-600 space-y-2 leading-relaxed">
                {(record?.referral_info?.status === 'IN_TRANSIT' || record?.volume_bonus_info?.status === 'IN_TRANSIT') && (
                  <p>• <span className="font-bold">在途赠送</span>：关联的推荐赠送（推荐人与被推荐人双向赠送）或达标赠送尚未结算，作废后将自动取消发放。</p>
                )}
                {record?.reward_ledgers && record.reward_ledgers.length > 0 && (
                  <p>• <span className="font-bold">已入账赠送</span>：该单据已有赠送结算入账。推荐人已有赠送结算入账不退回。</p>
                )}
              </div>
            </div>
          )}

          {/* 作废原因 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              作废原因 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="请详细说明作废原因..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          {/* 作废凭证上传 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              作废凭证
            </label>
            <ImageUploader
              value={proofUrls}
              onChange={setProofUrls}
              maxFiles={3}
              disabled={isSubmitting}
              uploadMeta={{
                imageType: '作废凭证',
                studentId: record?.student_id ? String(record.student_id) : '',
                studentName: record?.student_name,
                courseId: record?.course_id ? String(record.course_id) : '',
                courseName: record?.course_name
              }}
              onError={(error) => toast.error(`凭证上传失败: ${error}`)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            返回
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={isSubmitting || !reason.trim()}
          >
            {isSubmitting ? '作废中...' : '确认作废'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VoidRecordDialog;
